import React, { useId } from "react";
import { useTranslation } from "react-i18next";
import AppTooltip from "../../../../components/shared/AppTooltip/AppTooltip";
import { formatNumber, hasNumber, validRows } from "../../utils/analyticsFormat";
import styles from "../../OrderAnalyticsPage.module.scss";

export function ResourceState({ resource, children }) {
  const { t } = useTranslation();
  if (resource.status === "loading") {
    return (
      <div className={styles.state} role="status">
        <span className={styles.loader} />
        {t("analyticsNew.loading", "Загрузка аналитики…")}
      </div>
    );
  }
  if (resource.status === "error") {
    return (
      <div className={styles.state} role="alert">
        <span>{t("analyticsNew.error", "Не удалось загрузить данные")}</span>
        <button type="button" className={styles.retry} onClick={resource.retry}>
          {t("analyticsNew.retry", "Повторить")}
        </button>
      </div>
    );
  }
  return children(resource.data);
}

export function EmptyState() {
  const { t } = useTranslation();
  return <div className={styles.state}>{t("analyticsNew.empty", "Нет данных за выбранный период")}</div>;
}

export function Card({ title, hint, children }) {
  return (
    <section className={styles.card}>
      <h2>{title}</h2>
      {hint && <p className={styles.hint}>{hint}</p>}
      {children}
    </section>
  );
}

export function Kpi({ icon: Icon, color, label, value, detail }) {
  return (
    <div className={styles.kpi}>
      <span className={`${styles.kchip} ${styles[color]}`}>
        <Icon size={19} strokeWidth={2} aria-hidden="true" />
      </span>
      <div className={styles.kpiText}>
        <div className={styles.label}>{label}</div>
        <div className={styles.value}>{value}</div>
        {detail && <div className={styles.detail}>{detail}</div>}
      </div>
    </div>
  );
}

export function BarChart({
  data,
  valueKey = "count",
  series,
  title,
  color = "blue",
  percent = false,
}) {
  const { i18n } = useTranslation();
  const tooltipId = `analytics-bar-${useId().replace(/:/g, "")}`;
  const chartSeries =
    Array.isArray(series) && series.length ? series : [{ valueKey, color: "currentColor" }];
  const rows = validRows(data).filter((row) =>
    chartSeries.some((item) => hasNumber(row[item.valueKey])),
  );
  if (!rows.length) return <EmptyState />;
  const values = rows.flatMap((row) =>
    chartSeries.map((item) => row[item.valueKey]).filter(hasNumber),
  );
  const low = Math.min(0, ...values);
  const high = Math.max(1, ...values);
  const min = low < 0 ? low * 1.18 : 0;
  const max = high * 1.18;
  const y = (value) => 183 - ((value - min) / (max - min)) * 165;
  const baseline = y(0);
  const width = Math.max(760, rows.length * 28);
  const plotWidth = width - 60;
  const groupWidth = Math.min(56, (plotWidth / rows.length) * 0.72);
  const barGap = chartSeries.length > 1 ? 3 : 0;
  const barWidth = (groupWidth - barGap * (chartSeries.length - 1)) / chartSeries.length;
  const labelStep = Math.max(1, Math.ceil(rows.length / 14));
  const fmt = (value) => `${formatNumber(value, i18n.language, percent ? 1 : 0)}${percent ? "%" : ""}`;
  // Integer ticks keep sparse count charts from repeating 0 and 1 labels.
  const tickStep = percent ? (max - min) / 4 : Math.max(1, Math.ceil((max - min) / 4));
  const ticks = Array.from({ length: 5 }, (_, index) => min + tickStep * index).filter(
    (value) => value <= max,
  );
  return (
    <>
      {chartSeries.length > 1 && (
        <div className={styles.chartLegend} aria-label={title}>
          {chartSeries.map((item) => (
            <span key={item.valueKey}>
              <i style={{ backgroundColor: item.color }} aria-hidden="true" />
              {item.name}
            </span>
          ))}
        </div>
      )}
      <div className={styles.chartScroll}>
        <svg
          className={`${styles.chart} ${styles[color]}`}
          viewBox={`0 0 ${width} 210`}
          style={{ minWidth: width > 760 ? width : 420 }}
          role="img"
          aria-label={title}
        >
          <title>{title}</title>
          {ticks.map((value, index) => (
            <g key={index}>
              <line
                x1="44"
                x2={width - 16}
                y1={y(value)}
                y2={y(value)}
                stroke="#ECEEF4"
                strokeDasharray={value ? "3 4" : undefined}
              />
              <text x="36" y={y(value) + 4} textAnchor="end" className={styles.axis}>
                {fmt(value)}
              </text>
            </g>
          ))}
          {low < 0 && (
            <line x1="44" x2={width - 16} y1={baseline} y2={baseline} stroke="#98A1B2" />
          )}
          {rows.map((row, index) => {
            const centerX = 44 + (plotWidth * (index + 0.5)) / rows.length;
            const label = typeof row.label === "string" ? row.label : "—";
            return (
              <g key={`${row.period || label}-${index}`}>
                {chartSeries.map((item, seriesIndex) => {
                  const value = row[item.valueKey];
                  if (!hasNumber(value)) return null;
                  const x =
                    centerX - groupWidth / 2 + seriesIndex * (barWidth + barGap) + barWidth / 2;
                  const tooltipContent = item.name
                    ? `${item.name} · ${label}: ${fmt(value)}`
                    : `${label}: ${fmt(value)}`;
                  return (
                    <g key={item.valueKey}>
                      <rect
                        x={x - barWidth / 2}
                        y={Math.min(y(value), baseline)}
                        width={barWidth}
                        height={Math.abs(baseline - y(value))}
                        rx={Math.min(7, barWidth / 2)}
                        fill={item.color}
                        tabIndex="0"
                        aria-label={tooltipContent}
                        data-tooltip-id={tooltipId}
                        data-tooltip-content={tooltipContent}
                      />
                      {chartSeries.length === 1 && rows.length <= 14 && (
                        <text
                          x={x}
                          y={value < 0 ? y(value) + 13 : y(value) - 6}
                          textAnchor="middle"
                          className={styles.chartValue}
                        >
                          {fmt(value)}
                        </text>
                      )}
                    </g>
                  );
                })}
                {index % labelStep === 0 && (
                  <text x={centerX} y="204" textAnchor="middle" className={styles.axis}>
                    {label}
                  </text>
                )}
              </g>
            );
          })}
        </svg>
      </div>
      <AppTooltip id={tooltipId} place="top" positionStrategy="fixed" />
    </>
  );
}
